import React, { JSX, PropsWithChildren } from 'react'

import { Body1, Heading4 } from '@ui/Typography'
import { FeatureIcon } from '@ui/components/FeatureIcon/FeatureIcon'
import { CircleCheckIcon } from '@ui/icons/CircleCheck.icon'
import { ClockIcon } from '@ui/icons/Clock.icon'
import { InfoIcon } from '@ui/icons/Info.icon'
import { MailIcon } from '@ui/icons/Mail.icon'
import { MegaphoneIcon } from '@ui/icons/Megaphone.icon'
import { WarningIcon } from '@ui/icons/Warning.icon'

import { useWindowResize } from '@utils/domUtils'

import { Box } from '@shared/components/Box'

import styles from './StatusTemplate.module.scss'

interface Props {
  id?: string
  title: string
  text: (string | JSX.Element)[]
  icon: keyof typeof StatusTemplateIcon
}

export const StatusTemplateBody: React.FC<Props> = ({ title, text, icon, id }) => (
  <Box
    id={id}
    flex
    direction='column'
    align='center'
    textAlign='center'
    className={styles.StatusTemplateBody}
    bg='surface-white-smoke-1'
    radius='x56'
    gap='x16'
    p='x24'
  >
    {StatusTemplateIcon[icon]()}
    <Box className={styles.StatusTemplateBodyText} flex direction='column' gap='x16'>
      <Heading4>{title}</Heading4>
      <Box flex direction='column' gap='x12'>
        {text.map((item, index) => (
          <Body1 color='text-med-em' key={index}>
            {item}
          </Body1>
        ))}
      </Box>
    </Box>
  </Box>
)

interface StatusTemplateWrapper extends PropsWithChildren {
  banner?: React.ReactNode
}

export const StatusTemplateWrapper: React.FC<StatusTemplateWrapper> = ({ children, banner }) => {
  const isMobile = useWindowResize()

  return (
    <Box flex align='center' justify='center' fullWidth fullHeight={!isMobile} wrap gap='x24'>
      <StatusTemplateInnerWrapper>{children}</StatusTemplateInnerWrapper>
      {banner}
    </Box>
  )
}

export const StatusTemplateInnerWrapper: React.FC<PropsWithChildren> = ({ children }) => (
  <Box className={styles.StatusTemplateWrapper} flex direction='column' py='x16' gap='x32'>
    {children}
  </Box>
)

export const StatusTemplateButtons: React.FC<PropsWithChildren> = ({ children }) => {
  return (
    <Box flex align='center' fullWidth justify='center' direction='column' gap='x16'>
      {children}
    </Box>
  )
}

const StatusTemplateIcon = {
  Success: () => (
    <FeatureIcon
      size='lg'
      icon={CircleCheckIcon}
      bg='surface-success-high-em'
      color='text-inverse-black-neutral'
    />
  ),
  Warning: () => (
    <FeatureIcon size='lg' icon={WarningIcon} bg='surface-warning-high-em' color='text-black' />
  ),
  Fail: () => (
    <FeatureIcon
      size='lg'
      icon={WarningIcon}
      bg='surface-primary-med-em'
      color='text-inverse-black-neutral'
    />
  ),
  Mail: () => (
    <FeatureIcon
      size='lg'
      icon={MailIcon}
      color='text-inverse-black-neutral'
      bg='surface-info-p-high-em'
    />
  ),
  Info: () => (
    <FeatureIcon
      size='lg'
      icon={InfoIcon}
      color='text-inverse-black-neutral'
      bg='surface-info-p-high-em'
    />
  ),
  Promo: () => (
    <FeatureIcon
      size='lg'
      icon={MegaphoneIcon}
      color='text-inverse-black-neutral'
      bg='surface-primary-high-em'
    />
  ),
  Pending: () => (
    <FeatureIcon size='lg' icon={ClockIcon} color='text-warning-high-em' bg='surface-surface-1' />
  ),
}
